import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { WordTitle } from "./WordTitle";
import { VeroButton } from "./VeroButton";
import { IMAGES } from "../../constants/images";

export const ClassLiveSection = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [musicOn, setMusicOn] = useState(false);
  const inView = useInView(ref, { amount: 0.25 });

  useEffect(() => {
    if (!inView && musicOn) {
      audioRef.current?.pause();
      setMusicOn(false);
    }
  }, [inView, musicOn]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const toggleVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
    } else {
      v.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const toggleMusic = () => {
    const a = audioRef.current;
    if (!a) return;
    if (musicOn) {
      a.pause();
    } else {
      a.volume = 0.55;
      a.play().catch(() => {});
    }
    setMusicOn(!musicOn);
  };

  return (
    <section id="studio" ref={ref} className="relative h-screen overflow-hidden bg-black" data-testid="class-live-section">
      <img src={IMAGES.class_energy} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <video
        ref={videoRef}
        src="/videos/studio-class.mp4"
        poster={IMAGES.class_energy}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        aria-label="Apex Ride class in action"
        data-testid="class-live-video"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/50" />

      <audio ref={audioRef} src="/audio/apex-beat.mp3" loop preload="none" data-testid="class-live-audio" />

      <div className="absolute inset-x-0 bottom-0 z-10 px-6 md:px-14 pb-12 md:pb-16 flex flex-col md:flex-row md:items-end justify-between gap-10">
        <div>
          <p className="flex items-center gap-3 text-[0.7rem] uppercase tracking-[0.25em] text-zinc-300">
            <span className="relative flex w-2.5 h-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C5A059] opacity-60" />
              <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-[#C5A059]" />
            </span>
            {t.live.badge}
          </p>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-white">
            <WordTitle lines={t.live.title} />
          </h2>
          <div className="mt-10">
            <VeroButton text={t.live.cta} to="/book" testId="live-cta-btn" />
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0" data-testid="class-live-controls">
          <button
            onClick={toggleVideo}
            data-testid="class-video-toggle-btn"
            aria-label={playing ? "Pause video" : "Play video"}
            className="w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-white hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
          </button>
          <button
            onClick={toggleMusic}
            data-testid="class-music-toggle-btn"
            aria-pressed={musicOn}
            className={`flex items-center gap-2.5 rounded-full border px-5 py-3 text-[0.65rem] uppercase tracking-[0.2em] transition-colors duration-300 ${
              musicOn
                ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                : "border-white/40 text-white hover:border-[#C5A059] hover:text-[#C5A059]"
            }`}
          >
            {musicOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
            {musicOn ? t.live.musicOn : t.live.musicOff}
          </button>
        </div>
      </div>
    </section>
  );
};
